import { useCallback, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import logger from "@/utils/loggerUtil";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { actions, selectors } from "@/redux/authentication";
import useFetchUserDetails from "./useFetchUserDetails";
import { useAuth, useSession } from "@clerk/nextjs";

const useTokenManagement = (isLoggedIn) => {
  const { getToken } = useAuth();
  const { session } = useSession();
  const storedToken = useAppSelector(selectors.token);
  const dispatch = useAppDispatch();
  const { fetchUserDetails: loadUserDetails } = useFetchUserDetails();

  const tokenRefreshTimeout = useRef(null);
  const isInitializingRef = useRef(false);

  const refreshToken = useCallback(async () => {
    if (!isLoggedIn || isInitializingRef.current) return;

    try {
      isInitializingRef.current = true;

      if (!session?.status === "active") {
        await session?.reload();
      }

      const newToken = await getToken({
        template: process.env.NEXT_PUBLIC_CLERK_JWT_TEMPLATE,
      });

      if (newToken) {
        dispatch(actions.callSetAccessToken(newToken));
        // Schedule next refresh before token expires
        const payload = JSON.parse(atob(newToken.split(".")[1]));
        const timeUntilExpiry = payload.exp * 1000 - Date.now() - 60000; // Refresh 1 minute before expiry
        tokenRefreshTimeout.current = setTimeout(refreshToken, timeUntilExpiry);
      }
    } catch (error) {
      logger("Token refresh error:", error);
    } finally {
      isInitializingRef.current = false;
    }
  }, [isLoggedIn, getToken, session, dispatch]);

  // Initialize token management
  useEffect(() => {
    if (isLoggedIn && !storedToken) {
      refreshToken();
    }

    return () => {
      if (tokenRefreshTimeout.current) {
        clearTimeout(tokenRefreshTimeout.current);
      }
    };
  }, [isLoggedIn, storedToken, refreshToken]);

  // Handle user details fetch after token is available
  useEffect(() => {
    if (isLoggedIn && storedToken) {
      loadUserDetails();
    }
  }, [isLoggedIn, storedToken, loadUserDetails]);
};

export default useTokenManagement;
